class Product360Viewer extends HTMLElement {
  constructor() {
    super();
    this.CDN_BASE = 'https://evlovve-cdn.b-cdn.net/360-view';
    this.SENS = 4;
    this.CONCURRENCY_COLD = 16;
    this.CONCURRENCY_WARM = 30;
    this.s = {
      bitmaps: [],
      cache: {},
      currentId: null,
      meta: null,
      total: 120,
      frame: 0,
      ready: false,
      lastX: null,
      hintHidden: false,
      accum: 0,
      fullLoad: false,
      done: 0
    };
    this.variantMap = {};
    this._variantCheckBound = this._checkVariant.bind(this);
  }

  connectedCallback() {
    this.stage = this.querySelector('.viewer360-stage');
    this.canvas = this.querySelector('.viewer360-canvas');
    this.ctx = this.canvas.getContext('2d');
    this.loader = this.querySelector('.viewer360-loader');
    this.bar = this.querySelector('.viewer360-bar');
    this.pct = this.querySelector('.viewer360-pct');
    this.hint = this.querySelector('.viewer360-hint');

    this.variantMap = {};
    const mapScript = this.querySelector('.variant-map-json');
    if (mapScript) {
      try {
        this.variantMap = JSON.parse(mapScript.textContent);
      } catch (e) {}
    }

    this._resizeCanvas();
    window.addEventListener('resize', this._resizeCanvas.bind(this));

    if (window.ResizeObserver) {
      this._resizeObserver = new ResizeObserver(() => this._resizeCanvas());
      this._resizeObserver.observe(this.stage);
    }

    this.setupEvents();

    if (window.IntersectionObserver) {
      const observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          this._triggerFull();
          observer.unobserve(this);
        }
      }, { threshold: 0.1 });
      observer.observe(this);
    }

    document.addEventListener('change', this._variantCheckBound);
    document.addEventListener('variant:changed', (e) => {
      if (e.detail && e.detail.variant) {
        this._checkVariant(e.detail.variant.id);
      }
    });

    this._checkVariant();
  }

  disconnectedCallback() {
    document.removeEventListener('change', this._variantCheckBound);
    if (this._resizeObserver) this._resizeObserver.disconnect();
  }

  _resizeCanvas() {
    const rect = this.stage.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    this.canvas.width = rect.width * dpr;
    this.canvas.height = rect.height * dpr;
    this.canvas.style.width = '100%';
    this.canvas.style.height = '100%';
  }

  _checkVariant(forcedId) {
    let variantId = forcedId;
    if (!variantId) {
      const input = document.querySelector('[name="id"]');
      if (input) variantId = input.value;
    }
    if (!variantId) return;

    const viewerId = this.variantMap[variantId];
    if (viewerId) {
      this._loadViewer(viewerId);
    }
  }

  _navigateToSelf() {
    const swiper = this.closest('slide-with-thumbs');
    if (!swiper || typeof swiper.functionGoto !== 'function') return;
    const slides = swiper.querySelectorAll('.swiper-wrapper-preview .swiper-slide');
    const idx = Array.from(slides).indexOf(this);
    if (idx >= 0) swiper.functionGoto(idx);
  }

  _hideViewer() {
    this.stage.style.display = 'none';
    this.s.currentId = null;
    this.s.ready = false;
    this.s.frame = 0;
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.loader.classList.add('hidden');
    const thumb = this._findThumbnail();
    if (thumb) thumb.setAttribute('data-hidden', '');
  }

  _findThumbnail() {
    const wrapper = this.closest('slide-with-thumbs') || this.closest('media-gallery');
    if (!wrapper) return null;
    const thumbs = wrapper.querySelectorAll('.product-360-thumbnail');
    return thumbs.length ? thumbs[0] : null;
  }

  async _loadViewer(vid) {
    if (this.s.currentId === vid && this.s.ready) return;
    if (!vid) return;

    this.stage.style.display = 'block';
    this.s.currentId = vid;
    this.s.ready = false;
    this.s.frame = 0;
    this.s.lastX = null;
    this.s.accum = 0;
    this.s.fullLoad = false;
    this.s.done = 0;
    this.s.bitmaps = [];

    const thumb = this._findThumbnail();
    if (thumb) thumb.removeAttribute('data-hidden');

    if (this.s.cache[vid]) {
      this.s.bitmaps = this.s.cache[vid];
      this.s.ready = true;
      this.s.fullLoad = true;
      this.s.total = this.s.bitmaps.length;
      this._draw(0);
      this.loader.classList.add('hidden');
      return;
    }

    const meta = await this._fetchMeta(vid);
    if (this.s.currentId !== vid) return;

    this.s.meta = meta;
    this.s.total = meta.total;
    this.s.bitmaps = new Array(meta.total).fill(null);

    this._showLoader();
    this._loadFrame(0, vid);
    this._triggerFull();
  }

  async _fetchMeta(viewerId) {
    try {
      const res = await fetch(`${this.CDN_BASE}/${viewerId}/meta.json`);
      const json = await res.json();
      return {
        total: json.frames || 120,
        format: json.format || 'jpg',
        prefix: json.filePrefix || 'frame-'
      };
    } catch (e) {
      return { total: 120, format: 'jpg', prefix: 'frame-' };
    }
  }

  _frameUrl(id, i) {
    if (!this.s.meta) return '';
    const n = String(i + 1).padStart(3, '0');
    return `${this.CDN_BASE}/${id}/${this.s.meta.prefix}${n}.${this.s.meta.format}`;
  }

  _showLoader() {
    this.loader.classList.remove('hidden');
    this.bar.style.width = '0%';
    this.pct.textContent = '0%';
  }

  _draw(f) {
    if (!this.s.bitmaps[f]) return;
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.ctx.drawImage(this.s.bitmaps[f], 0, 0, this.canvas.width, this.canvas.height);
  }

  _updateProgress(vid) {
    if (!this.s.fullLoad) return;
    const p = Math.round((this.s.done / this.s.total) * 100);
    this.bar.style.width = p + '%';
    this.pct.textContent = p + '%';

    if (this.s.done >= Math.min(12, this.s.total) && !this.s.ready) {
      this.s.ready = true;
    }

    if (this.s.done === this.s.total && this.s.currentId === vid) {
      this.s.cache[vid] = this.s.bitmaps.slice();
      this.loader.classList.add('hidden');
    }
  }

  _loadFrame(idx, vid) {
    if (this.s.bitmaps[idx]) return Promise.resolve();
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        createImageBitmap(img).then((bmp) => {
          if (this.s.currentId !== vid) return resolve();
          this.s.bitmaps[idx] = bmp;
          this.s.done++;
          this._updateProgress(vid);
          if (idx === 0) {
            this._draw(0);
            this.loader.classList.add('hidden');
          }
          resolve();
        });
      };
      img.onerror = () => {
        this.s.done++;
        this._updateProgress(vid);
        resolve();
      };
      img.src = this._frameUrl(vid, idx);
    });
  }

  _triggerFull() {
    if (this.s.fullLoad) return;
    if (!this.s.meta) return;

    this.s.fullLoad = true;

    const vid = this.s.currentId;
    if (!vid) return;

    const total = this.s.total;
    const seen = new Set();
    const queue = [];
    for (const step of [10, 5, 2, 1]) {
      for (let i = 0; i < total; i += step) {
        if (!seen.has(i)) { seen.add(i); queue.push(i); }
      }
    }

    const concurrency = this.s.cache[vid] ? this.CONCURRENCY_WARM : this.CONCURRENCY_COLD;
    let idx = 0;

    const worker = async () => {
      while (idx < queue.length) {
        if (this.s.currentId !== vid) return;
        const frameIdx = queue[idx++];
        await this._loadFrame(frameIdx, vid);
      }
    };

    Promise.all(Array.from({ length: concurrency }, worker));
  }

  setupEvents() {
    this.stage.addEventListener('mousedown', (e) => {
      this._triggerFull();
      this.s.lastX = e.clientX;
      this.s.accum = 0;
      this.stage.style.cursor = 'grabbing';
      e.preventDefault();
    });

    document.addEventListener('mousemove', (e) => {
      if (this.s.lastX === null) return;
      this._triggerFull();
      if (!this.s.ready) return;
      this.s.accum += e.clientX - this.s.lastX;
      this.s.lastX = e.clientX;
      if (Math.abs(this.s.accum) >= this.SENS) {
        const steps = Math.floor(Math.abs(this.s.accum) / this.SENS);
        const dir = this.s.accum > 0 ? 1 : -1;
        this.s.frame = (this.s.frame + dir * steps + this.s.total) % this.s.total;
        this.s.accum = this.s.accum % this.SENS;
        this._draw(this.s.frame);
        if (!this.s.hintHidden) {
          this.s.hintHidden = true;
          this.hint.classList.add('hidden');
        }
      }
    });

    document.addEventListener('mouseup', () => {
      this.s.lastX = null;
      this.s.accum = 0;
      this.stage.style.cursor = 'grab';
    });

    this.stage.addEventListener('touchstart', (e) => {
      this._triggerFull();
      this.s.lastX = e.touches[0].clientX;
      this.s.accum = 0;
    }, { passive: true });

    this.stage.addEventListener('touchmove', (e) => {
      this._triggerFull();
      if (!this.s.ready || this.s.lastX === null) return;
      const dx = e.touches[0].clientX - this.s.lastX;
      if (Math.abs(dx) > 5) {
        e.preventDefault();
        e.stopPropagation();
        this.s.accum += dx;
        this.s.lastX = e.touches[0].clientX;
        if (Math.abs(this.s.accum) >= this.SENS) {
          const steps = Math.floor(Math.abs(this.s.accum) / this.SENS);
          const dir = this.s.accum > 0 ? 1 : -1;
          this.s.frame = (this.s.frame + dir * steps + this.s.total) % this.s.total;
          this.s.accum = this.s.accum % this.SENS;
          this._draw(this.s.frame);
          if (!this.s.hintHidden) {
            this.s.hintHidden = true;
            this.hint.classList.add('hidden');
          }
        }
      }
    }, { passive: false });

    this.stage.addEventListener('touchend', () => {
      this.s.lastX = null;
      this.s.accum = 0;
    });

    this.stage.addEventListener('wheel', (e) => {
      this._triggerFull();
      if (!this.s.ready) return;
      const dx = Math.abs(e.deltaX), dy = Math.abs(e.deltaY);
      if (dx > dy || dy > dx) {
        e.preventDefault();
        e.stopPropagation();
        const dir = dx >= dy ? (e.deltaX > 0 ? 1 : -1) : (e.deltaY > 0 ? 1 : -1);
        this.s.frame = (this.s.frame + dir + this.s.total) % this.s.total;
        this._draw(this.s.frame);
        if (!this.s.hintHidden) {
          this.s.hintHidden = true;
          this.hint.classList.add('hidden');
        }
      }
    }, { passive: false });
  }
}

customElements.define('product-360-viewer', Product360Viewer);
